/**
 * AgentVault OpenClaw Channel Plugin
 *
 * Registers AgentVault as a first-class OpenClaw messaging channel.
 * Messages from the AgentVault UI are decrypted and routed into the
 * OpenClaw agent loop; agent replies are encrypted and sent back.
 *
 * Installation (one-time):
 *   npx @agentvault/agentvault setup --token=av_tok_...
 *
 * After setup, restart OpenClaw — no further configuration needed.
 */

import { resolve } from "node:path";
import { SecureChannel } from "./channel.js";
import { listAccountIds, resolveAccount, type ResolvedAccount } from "./account-config.js";
import { handleSendRequest, handleActionRequest, handleStatusRequest } from "./http-handlers.js";
import { requestHeartbeatNow } from "./openclaw-compat.js";
import { attachLifecycle, formatChannelError } from "./lifecycle.js";
import type { MessageMetadata } from "./types.js";
import type {
  PluginRuntime,
  ChannelGatewayContext,
  ChannelOutboundPayloadContext,
} from "./openclaw-types.js";

// Runtime injected by register() — used in inbound handler
let _ocRuntime: PluginRuntime | null = null;
// Active channel instances keyed by accountId
const _channels = new Map<string, SecureChannel>();
/** Whether OpenClaw managed HTTP routes are active. */
let _usingManagedRoutes = false;

export function setOcRuntime(runtime: PluginRuntime): void {
  _ocRuntime = runtime;
}

export function getActiveChannel(accountId = "default"): SecureChannel | undefined {
  return _channels.get(accountId);
}

// ---------------------------------------------------------------------------
// Channel plugin definition
// ---------------------------------------------------------------------------

export const agentVaultPlugin = {
  id: "agentvault",
  meta: {
    id: "agentvault",
    label: "AgentVault",
    selectionLabel: "AgentVault (E2E Encrypted)",
    docsPath: "https://agentvault.chat/docs",
    blurb: "End-to-end encrypted messaging between owners and their AI agents.",
    aliases: ["av", "agent-vault"],
  },
  capabilities: {
    chatTypes: ["direct", "room"],
  },
  config: {
    listAccountIds,
    resolveAccount,
  },
  gateway: {
    /** Health probe for `openclaw channels status --probe` */
    probe: async (ctx: any) => {
      const accountId = ctx?.accountId ?? "default";
      const ch = _channels.get(accountId);
      if (!ch) return { ok: false, status: "disconnected", error: "Channel not started" };
      const state = ch.state;
      return {
        ok: state === "ready",
        status: state,
        deviceId: ch.deviceId ?? undefined,
        sessions: ch.sessionCount,
      };
    },

    /** Status for `openclaw health --json` per-channel summary */
    status: (ctx: any) => {
      const accountId = ctx?.accountId ?? "default";
      const ch = _channels.get(accountId);
      if (!ch) return { connected: false, status: "not_started" };
      return {
        connected: ch.state === "ready",
        status: ch.state,
        deviceId: ch.deviceId ?? undefined,
        sessions: ch.sessionCount,
        encrypted: true,
      };
    },

    startAccount: async (ctx: ChannelGatewayContext<ResolvedAccount>) => {
      const { account, cfg, log, abortSignal } = ctx;
      const _log: ((msg: string) => void) | undefined = typeof log === "function" ? log : log?.info?.bind(log);

      if (!account.configured) {
        throw new Error(
          "AgentVault channel is not configured.\n" +
            "Run the one-time setup: npx @agentvault/agentvault setup --token=av_tok_...\n" +
            "Then restart OpenClaw: openclaw gateway restart",
        );
      }

      const dataDir = resolve(account.dataDir.replace(/^~/, process.env.HOME ?? "~"));

      _log?.(`[AgentVault] starting channel (dataDir=${dataDir})`);

      // startAccount must STAY PENDING while the channel is running.
      // Resolving signals "channel stopped" to the gateway health monitor,
      // which triggers auto-restart. We block here until the abortSignal
      // fires (gateway shutdown / config reload), then clean up.
      await new Promise<void>((resolvePromise, reject) => {
        // #395: set when a TERMINAL auth failure is handled — suppresses the
        // onStateChange "error" → reject below (which the host treats as
        // "restart this account", re-entering the crash-loop on dead creds).
        let terminalStopped = false;

        const channel = new SecureChannel({
          inviteToken: "",
          dataDir,
          apiUrl: account.apiUrl,
          agentName: account.agentName,

          onMessage: async (plaintext: string, metadata: MessageMetadata) => {
            if (!_ocRuntime) {
              _log?.("[AgentVault] runtime not ready — dropping inbound message");
              return;
            }
            try {
              await _handleInbound({ plaintext, metadata, channel, account, cfg });
            } catch (err) {
              _log?.(`[AgentVault] inbound dispatch error: ${String(err)}`);
            }
          },

          onStateChange: (state: string) => {
            _log?.(`[AgentVault] state → ${state}`);
            // #395: defer the restart-reject one microtask and guard on
            // terminalStopped — a terminal event emitted in the same tick (right
            // after _setState("error")) pre-empts the restart, so a revoked
            // device goes idle instead of crash-looping. Non-terminal "error"
            // states still reject→restart (existing recovery).
            if (state === "error") {
              queueMicrotask(() => {
                if (!terminalStopped) reject(new Error("AgentVault channel permanent error"));
              });
            }
          },
        });

        _channels.set(account.accountId, channel);

        // Prevent unhandled "error" events from crashing the process
        channel.on("error", (err: Error) => {
          // #718: "non-fatal" was true of the PROCESS and false of the MESSAGE.
          // A server rejection reaching here means a frame already logged as
          // `result=sent` was refused and dropped; filing that under the same
          // wording as a transient blip is how 707 of them went unread.
          _log?.(formatChannelError(err));
        });

        // #395: terminal auth failure stops THIS agent's channel + goes idle
        // (the process + other agents stay up). Leaves the account promise
        // pending so the host does NOT auto-restart into the dead creds.
        attachLifecycle(channel, {
          agentName: account.agentName,
          log: (m: string) => _log?.(`[AgentVault] ${m}`),
          onTerminal: async () => {
            terminalStopped = true;
            try {
              await channel.stop();
            } catch {
              /* stop is best-effort */
            }
            // Drop the dead channel from the routing map (outbound sends read it).
            _channels.delete(account.accountId);
            // Leave the account promise PENDING (idle) — settling would signal
            // the host to auto-restart into the dead creds.
          },
        });

        // Always start local HTTP server — managed routes are an overlay, not a replacement
        const httpPort = account.httpPort;
        channel.on("ready", () => {
          channel.startHttpServer(httpPort);
          _log?.(`[AgentVault] HTTP send server listening on http://127.0.0.1:${httpPort}`);
        });

        // Respect gateway abort signal (OpenClaw gateway shutdown)
        abortSignal?.addEventListener("abort", async () => {
          await channel.stop();
          _channels.delete(account.accountId);
          resolvePromise();
        });

        // Guard the startup-reject with terminalStopped too (a terminal surfaced
        // as a start() rejection must not reject→restart into dead creds).
        channel.start().catch((e: unknown) => {
          if (!terminalStopped) reject(e);
        });
      });

      return { stop: async () => {} };
    },
  },
  outbound: {
    deliveryMode: "direct" as const,
    targets: [
      {
        id: "owner",
        label: "AgentVault Owner",
        accountId: "default",
      },
      {
        id: "default",
        label: "AgentVault Owner (default)",
        accountId: "default",
      },
    ],
    sendText: async ({
      text,
      accountId,
      targetId,
    }: {
      text: string;
      accountId?: string;
      targetId?: string;
    }): Promise<{ ok: boolean; error?: string }> => {
      const resolvedId = accountId ?? (targetId === "owner" ? "default" : (targetId ?? "default"));
      const channel = _channels.get(resolvedId);
      if (!channel) {
        return { ok: false, error: "AgentVault channel is not connected" };
      }
      try {
        await channel.send(text);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },

    sendMedia: async ({
      text,
      mediaUrl,
      accountId,
    }: {
      to: string;
      text?: string;
      mediaUrl: string;
      accountId?: string;
    }): Promise<{ ok: boolean; error?: string }> => {
      const resolvedId = accountId ?? "default";
      const channel = _channels.get(resolvedId);
      if (!channel) {
        return { ok: false, error: "AgentVault channel is not connected" };
      }
      try {
        const message = text ? `${text}\n${mediaUrl}` : mediaUrl;
        await channel.send(message);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },

    /** Rich payload delivery — OpenClaw v2026.3.2+ */
    sendPayload: async (ctx: ChannelOutboundPayloadContext) => {
      const { payload, accountId } = ctx;
      const resolvedId = accountId ?? "default";
      const channel = _channels.get(resolvedId);
      if (!channel) return { ok: false, error: "AgentVault channel is not connected" };

      try {
        if (payload.isReasoning) return { ok: true };

        requestHeartbeatNow({ reason: "outbound-payload" }).catch(() => {});

        const text = (payload.text ?? "").trim();
        if (!text && !payload.mediaUrls?.length) return { ok: true };

        if (text) await channel.send(text);
        if (payload.mediaUrls?.length) {
          for (const url of payload.mediaUrls) await channel.send(url);
        }
        if (payload.suggestedActions?.length) {
          const actionsText = payload.suggestedActions
            .map((a) => `- ${a.label}: ${a.action}`)
            .join("\n");
          await channel.send(`Suggested actions:\n${actionsText}`);
        }
        return { ok: true };
      } catch (err) {
        return { ok: false, error: String(err) };
      }
    },
  },
};

// ---------------------------------------------------------------------------
// Inbound message handler — routes decrypted messages into the agent loop
// ---------------------------------------------------------------------------

async function _handleInbound(params: {
  plaintext: string;
  metadata: MessageMetadata;
  channel: SecureChannel;
  account: ResolvedAccount;
  cfg: any;
}): Promise<void> {
  const { plaintext, metadata, channel, account, cfg } = params;
  const core = _ocRuntime!; // Non-null: caller guards with `if (!_ocRuntime)` check

  // Stable session key scoped to this AgentVault conversation
  const sessionKey = `agentvault:${account.accountId}:${(metadata.conversationId ?? "default").slice(0, 8)}`;
  const senderId = `agentvault:owner`;

  // Resolve agent routing (respects agents.list routing rules if configured)
  const route = core.channel.routing.resolveAgentRoute({
    cfg,
    channel: "agentvault",
    accountId: account.accountId,
    peer: { kind: "direct", id: senderId },
  });

  const storePath = core.channel.session.resolveStorePath(cfg?.session?.store, {
    agentId: route.agentId,
  });

  const envelopeOptions = core.channel.reply.resolveEnvelopeFormatOptions(cfg);
  const previousTimestamp = core.channel.session.readSessionUpdatedAt({
    storePath,
    sessionKey: route.sessionKey,
  });

  // Format the message body with standard OpenClaw envelope (channel label, timestamps, etc.)
  const body = core.channel.reply.formatAgentEnvelope({
    channel: "AgentVault",
    from: "Owner",
    timestamp: new Date(metadata.timestamp).getTime(),
    previousTimestamp,
    envelope: envelopeOptions,
    body: plaintext,
  });

  // Build the inbound context payload OpenClaw expects
  const ctxPayload = core.channel.reply.finalizeInboundContext({
    Body: body,
    RawBody: plaintext,
    CommandBody: plaintext,
    From: senderId,
    To: `agentvault:agent:${account.accountId}`,
    SessionKey: route.sessionKey,
    AccountId: account.accountId,
    ChatType: "direct",
    ConversationLabel: "AgentVault",
    SenderName: "Owner",
    SenderId: senderId,
    Provider: "agentvault",
    Surface: "agentvault",
    MessageSid: metadata.messageId,
    Timestamp: new Date(metadata.timestamp).getTime(),
    OriginatingChannel: "agentvault",
    OriginatingTo: `agentvault:agent:${account.accountId}`,
    // Owner is cryptographically verified via X3DH + Double Ratchet enrollment
    CommandAuthorized: true,
  });

  // Record session metadata for context continuity
  await core.channel.session.recordInboundSession({
    storePath,
    sessionKey: ctxPayload.SessionKey ?? route.sessionKey,
    ctx: ctxPayload,
    onRecordError: (err: Error) => {
      core.error?.(`[AgentVault] session record failed: ${String(err)}`);
    },
  });

  // Dispatch to the OpenClaw agent loop; deliver replies back through the channel.
  // #1418: as this conversation's turn, so the untargeted replies below (and
  // anything else the agent sends meanwhile) reach this person only.
  await channel.runForConversation(metadata.conversationId, () => core.channel.reply.dispatchReplyWithBufferedBlockDispatcher({
    ctx: ctxPayload,
    cfg,
    dispatcherOptions: {
      deliver: async (payload: { text?: string; mediaUrls?: string[] }) => {
        const text = (payload.text ?? "").trim();
        if (text) {
          await channel.send(text);
        }
      },
      onError: (err: Error, info?: { kind?: string }) => {
        core.error?.(
          `[AgentVault] ${info?.kind ?? "reply"} error: ${String(err)}`,
        );
      },
    },
    replyOptions: {},
  }), { groupId: metadata.conversationGroupId });
}
